/**
 * useDynamicChart Hook
 *
 * Fetches and manages historical data for any KPI
 * Handles time range selection, data fetching, and statistics calculation
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { subHours, subDays } from 'date-fns';
import api from '../services/api';
import { getKPIMetadata } from '../utils/kpiMetadata';
import type {
  AssetType,
  TimeSeriesDataPoint,
  ChartStatistics,
} from '../types/chart.types';

export type TimeRange = '1h' | '24h' | '7d' | '30d' | '90d';

interface UseDynamicChartProps {
  siteId: string;
  kpiName: string;
  assetType: AssetType;
  enabled: boolean;
}

export function useDynamicChart({
  siteId,
  kpiName,
  assetType,
  enabled,
}: UseDynamicChartProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');

  // Get KPI metadata
  const metadata = useMemo(() => getKPIMetadata(kpiName), [kpiName]);

  // Calculate time range
  const getTimeRange = () => {
    const end = new Date();
    let start: Date;

    switch (timeRange) {
      case '1h':
        start = subHours(end, 1);
        break;
      case '24h':
        start = subHours(end, 24);
        break;
      case '7d':
        start = subDays(end, 7);
        break;
      case '30d':
        start = subDays(end, 30);
        break;
      case '90d':
        start = subDays(end, 90);
        break;
      default:
        start = subDays(end, 7);
    }

    return { start, end };
  };

  // Determine interval based on time range
  const getInterval = () => {
    switch (timeRange) {
      case '1h':
        return '1min';
      case '24h':
        return '5min';
      case '7d':
        return '1hour';
      case '30d':
        return '1hour';
      case '90d':
        return '1day';
      default:
        return '1hour';
    }
  };

  // Fetch data
  const { data, isLoading, error } = useQuery({
    queryKey: ['dynamic-chart', siteId, kpiName, assetType, timeRange],
    queryFn: async () => {
      const { start, end } = getTimeRange();
      const interval = getInterval();

      const response = await api.get(
        `/v1/sites/${siteId}/timeseries?` +
          `asset_type=${assetType}&` +
          `kpis=${kpiName}&` +
          `start=${start.toISOString()}&` +
          `end=${end.toISOString()}&` +
          `interval=${interval}&` +
          `aggregation=avg`
      );

      return response.data.data.series[kpiName] || [];
    },
    refetchInterval: 30000, // 30 seconds
    enabled: enabled && !!siteId && !!kpiName,
  });

  // Calculate statistics
  const statistics = useMemo<ChartStatistics | undefined>(() => {
    if (!data || data.length === 0) return undefined;

    const values = data.map((d: TimeSeriesDataPoint) => d.value);
    const timestamps = data.map((d: TimeSeriesDataPoint) => new Date(d.timestamp));

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const avgValue = values.reduce((sum: number, v: number) => sum + v, 0) / values.length;

    const minIndex = values.indexOf(minValue);
    const maxIndex = values.indexOf(maxValue);

    // Calculate trend (compare first and last 10% of data)
    const trendSampleSize = Math.max(1, Math.floor(data.length * 0.1));
    const firstSample = values.slice(0, trendSampleSize);
    const lastSample = values.slice(-trendSampleSize);
    const firstAvg = firstSample.reduce((sum: number, v: number) => sum + v, 0) / firstSample.length;
    const lastAvg = lastSample.reduce((sum: number, v: number) => sum + v, 0) / lastSample.length;
    const trendValue = firstAvg !== 0 ? ((lastAvg - firstAvg) / firstAvg) * 100 : 0;

    return {
      min: { value: minValue, timestamp: timestamps[minIndex] },
      max: { value: maxValue, timestamp: timestamps[maxIndex] },
      avg: avgValue,
      trend: {
        direction: trendValue > 1 ? 'up' : trendValue < -1 ? 'down' : 'stable',
        value: trendValue,
      },
    };
  }, [data]);

  return {
    data: data || [],
    isLoading,
    error,
    timeRange,
    setTimeRange,
    metadata,
    statistics,
  };
}
